import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Typography,
    Box,
    Tab,
    Tabs,
    CircularProgress,
    Alert,
    Tooltip,
    Button
} from '@mui/material';
import {
    Close as CloseIcon,
    Download as DownloadIcon,
    TextFields as TextIcon,
    Code as CodeIcon,
    Image as ImageIcon,
    PictureAsPdf as PdfIcon,
    Description as DocIcon,
    Note as NoteIcon
} from '@mui/icons-material';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';
import { DocumentEditor } from '@onlyoffice/document-editor-react';
import Editor from '@monaco-editor/react';
import authService from '../services/auth.service';
import ImageViewer from './ImageViewer';
import DocumentNotes from './DocumentNotes';

interface DocumentViewerProps {
    open: boolean;
    onClose: () => void;
    document: any;
}

const DocumentViewer: React.FC<DocumentViewerProps> = ({ open, onClose, document }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState(0);
    const [documentUrl, setDocumentUrl] = useState<string | null>(null);
    const [textContent, setTextContent] = useState<string>('');
    const [ocrContent, setOcrContent] = useState<string>('');
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [onlyOfficeConfig, setOnlyOfficeConfig] = useState<any>(null);
    const [notesOpen, setNotesOpen] = useState(false);

    // Create default layout plugin instance
    const defaultLayoutPluginInstance = defaultLayoutPlugin();

    const getOnlyOfficeConfig = async () => {
        if (!document) return null;

        const fileType = getFileType();
        const documentType = fileType === 'word' ? 'word' : 'cell';
        const fileExtension = document.fileSyncId.fileName.split('.').pop()?.toLowerCase() || 'docx';

        // Get the API URL for the document
        const api = authService.getApiInstance();
        const baseUrl = api.defaults.baseURL || '';
        const documentFileUrl = `${baseUrl}/documents/${document._id}/onlyoffice?token=${authService.getAccessToken()}`;
        let configInfo: any = {
            documentServerUrl: 'https://office.bluelot.net',
            config: {
                document: {
                    fileType: fileExtension,
                    key: `${document._id}_${Date.now()}`,
                    title: document.fileSyncId.fileName,
                    url: documentFileUrl
                },
                documentType: documentType,
                type: 'embedded',
                editorConfig: {
                    mode: 'view'
                },
                permissions: {
                    edit: false
                },
                width: '100%',
                height: '100%'
            }
        };

        const token = await authService.getOnlyOfficeToken(configInfo);
        configInfo.config.token = token;
        setOnlyOfficeConfig(configInfo);
    };

    useEffect(() => {
        if (open && document) {
            setActiveTab(0); // Reset to original file tab
            setTextContent('');
            setOcrContent('');
            loadDocument();
        }
        return () => {
            if (documentUrl) {
                URL.revokeObjectURL(documentUrl);
            }
        };
    }, [open, document]);

    const loadDocument = async () => {
        try {
            setLoading(true);
            setError(null);
            const api = authService.getApiInstance();

            // Load document content
            try {
                const contentResponse = await api.get(`/documents/${document._id}/content`);
                if (contentResponse.data.extractedText) {
                    setTextContent(contentResponse.data.extractedText);
                }
                if (contentResponse.data.ocrText) {
                    setOcrContent(contentResponse.data.ocrText);
                }
            } catch (contentError: any) {
                console.log('No text content available for this document');
                setTextContent('');
                setOcrContent('');
            }

            // Get document file URL for preview
            const response = await api.get(`/documents/${document._id}/preview`, {
                responseType: 'blob'
            });
            const url = URL.createObjectURL(response.data);
            setDocumentUrl(url);
            await getOnlyOfficeConfig();
        } catch (err: any) {
            setError(err.response?.data?.error || 'Failed to load document');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async () => {
        try {
            const api = authService.getApiInstance();
            const response = await api.get(`/documents/${document._id}/download`, {
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = window.document.createElement('a');
            link.href = url;
            link.setAttribute('download', document.fileSyncId.fileName);
            window.document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    const getFileType = () => {
        const mimeType = document.fileSyncId?.mimeType?.toLowerCase() || '';
        const fileName = document.fileSyncId?.fileName?.toLowerCase() || '';

        if (mimeType.includes('pdf')) return 'pdf';
        if (mimeType.includes('image')) return 'image';
        if (mimeType.includes('word') || fileName.endsWith('.docx') || fileName.endsWith('.doc')) return 'word';
        if (
            mimeType.includes('sheet') ||
            mimeType.includes('excel') ||
            fileName.endsWith('.xlsx') ||
            fileName.endsWith('.xls')
        )
            return 'excel';
        if (mimeType.includes('text') || fileName.endsWith('.txt')) return 'text';
        if (
            fileName.endsWith('.js') ||
            fileName.endsWith('.ts') ||
            fileName.endsWith('.jsx') ||
            fileName.endsWith('.tsx') ||
            fileName.endsWith('.json') ||
            fileName.endsWith('.xml') ||
            fileName.endsWith('.html') ||
            fileName.endsWith('.css') ||
            fileName.endsWith('.py') ||
            fileName.endsWith('.java') ||
            fileName.endsWith('.cpp') ||
            fileName.endsWith('.c')
        )
            return 'code';
        return 'unknown';
    };

    const getLanguageFromFileName = (fileName: string): string => {
        const ext = fileName.split('.').pop()?.toLowerCase();
        const languageMap: { [key: string]: string } = {
            js: 'javascript',
            jsx: 'javascript',
            ts: 'typescript',
            tsx: 'typescript',
            json: 'json',
            html: 'html',
            css: 'css',
            scss: 'scss',
            py: 'python',
            java: 'java',
            cpp: 'cpp',
            c: 'c',
            cs: 'csharp',
            php: 'php',
            rb: 'ruby',
            go: 'go',
            rs: 'rust',
            kt: 'kotlin',
            swift: 'swift',
            sql: 'sql',
            xml: 'xml',
            yaml: 'yaml',
            yml: 'yaml',
            md: 'markdown',
            sh: 'shell',
            bash: 'shell'
        };
        return languageMap[ext || ''] || 'plaintext';
    };

    const renderViewer = () => {
        const fileType = getFileType();

        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (error) {
            return <Alert severity="error">{error}</Alert>;
        }

        // Determine which tab is being viewed
        let currentView = 'original';
        if (activeTab === 1) {
            currentView = 'ocr';
        }

        switch (currentView) {
            case 'original': // Original File View
                switch (fileType) {
                    case 'pdf':
                        return documentUrl ? (
                            <Box sx={{ height: '600px' }}>
                                <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js">
                                    <Viewer fileUrl={documentUrl} plugins={[defaultLayoutPluginInstance]} />
                                </Worker>
                            </Box>
                        ) : null;

                    case 'image':
                        return (
                            <Box sx={{ textAlign: 'center', p: 2 }}>
                                <Button
                                    variant="contained"
                                    startIcon={<ImageIcon />}
                                    onClick={() => setImageViewerOpen(true)}
                                >
                                    Open Image Viewer
                                </Button>
                                {documentUrl && (
                                    <Box sx={{ mt: 2 }}>
                                        <img
                                            src={documentUrl}
                                            alt={document.fileSyncId.fileName}
                                            style={{ maxWidth: '100%', maxHeight: '500px' }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        );

                    case 'word':
                    case 'excel':
                        return onlyOfficeConfig ? (
                            <Box sx={{ height: '600px', width: '100%', position: 'relative' }}>
                                <div className="w-full h-full">
                                    <DocumentEditor
                                        id="onlyoffice-viewer"
                                        documentServerUrl={onlyOfficeConfig.documentServerUrl}
                                        config={onlyOfficeConfig.config}
                                        documentType={onlyOfficeConfig.config?.documentType}
                                        events_onDocumentReady={() => console.log('Document is loaded')}
                                        onLoadComponentError={(err: any) => {
                                            console.error('OnlyOffice load error:', err);
                                        }}
                                    />
                                </div>
                            </Box>
                        ) : (
                            <Alert severity="error">Failed to load document configuration</Alert>
                        );

                    case 'text':
                    case 'code':
                        return textContent ? (
                            <Box sx={{ height: '600px' }}>
                                <Editor
                                    defaultLanguage={
                                        fileType === 'code'
                                            ? getLanguageFromFileName(document.fileSyncId.fileName)
                                            : 'plaintext'
                                    }
                                    value={textContent}
                                    theme="vs-dark"
                                    options={{
                                        readOnly: true,
                                        minimap: { enabled: false },
                                        scrollBeyondLastLine: false,
                                        fontSize: 14
                                    }}
                                />
                            </Box>
                        ) : (
                            <Alert severity="warning">No text content available</Alert>
                        );

                    default:
                        return (
                            <Box sx={{ p: 3, textAlign: 'center' }}>
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    Preview not available for this file type.
                                </Alert>
                                <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownload}>
                                    Download File
                                </Button>
                            </Box>
                        );
                }

            case 'ocr': // OCR Text tab - shows either extracted text or OCR text
                const displayText = textContent || ocrContent;
                return displayText ? (
                    <Box sx={{ p: 2, height: '600px', overflow: 'auto' }}>
                        <Typography
                            variant="body2"
                            component="pre"
                            sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
                        >
                            {displayText}
                        </Typography>
                        {!textContent && document.ocrConfidence && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                                OCR Confidence: {document.ocrConfidence}%
                            </Typography>
                        )}
                    </Box>
                ) : (
                    <Alert severity="info">No text content available</Alert>
                );

            default:
                return null;
        }
    };

    const getIcon = () => {
        const fileType = getFileType();
        switch (fileType) {
            case 'pdf':
                return <PdfIcon />;
            case 'image':
                return <ImageIcon />;
            case 'word':
            case 'excel':
                return <DocIcon />;
            case 'text':
            case 'code':
                return <CodeIcon />;
            default:
                return <TextIcon />;
        }
    };

    return (
        <>
            <Dialog
                open={open && !imageViewerOpen}
                onClose={onClose}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { height: '90vh' }
                }}
            >
                <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center' }}>
                    {getIcon()}
                    <Typography sx={{ ml: 1, flex: 1 }}>
                        {document?.fileSyncId?.fileName || 'Document Viewer'}
                    </Typography>
                    <Tooltip title="Notes">
                        <IconButton onClick={() => setNotesOpen(true)} sx={{ mr: 1 }} aria-label="Notes">
                            <NoteIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                        <IconButton onClick={handleDownload} sx={{ mr: 1 }}>
                            <DownloadIcon />
                        </IconButton>
                    </Tooltip>
                    <IconButton aria-label="close" onClick={onClose} sx={{ color: (theme) => theme.palette.grey[500] }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={activeTab} onChange={(_, newValue) => setActiveTab(newValue)}>
                        <Tab label="Original File" />
                        {(textContent || ocrContent) && <Tab label="OCR Text" />}
                    </Tabs>
                </Box>
                <DialogContent sx={{ p: 0 }}>{renderViewer()}</DialogContent>
            </Dialog>

            {/* Image Viewer Modal */}
            {document && (
                <ImageViewer
                    open={imageViewerOpen}
                    onClose={() => setImageViewerOpen(false)}
                    documentId={document._id}
                    fileName={document.fileSyncId.fileName}
                    fileSize={document.fileSyncId.fileSize}
                    metadata={document.metadata}
                />
            )}
            
            {/* Document Notes Modal */}
            {document && (
                <DocumentNotes
                    open={notesOpen}
                    onClose={() => setNotesOpen(false)}
                    documentId={document._id}
                />
            )}
        </>
    );
};

export default DocumentViewer;
